/**
 * Aula invertida (M5) — query builders drizzle del plan de clase.
 * Consumo local-first sobre los streams de cohorte (session_module rueda con
 * my_cohorts_student/teacher/campus/org). Escritura vía write-path (mutations.ts).
 */

import {
  and,
  asc,
  assignment,
  class_session,
  course_module,
  eq,
  isNull,
  lesson,
  type PowerSyncDrizzleDB,
  resource,
  session_module,
  video_asset,
} from "@space/powersync";

export const sessionPlanQueryKeys = {
  bySession: (sessionId: string | undefined) => ["session-plan", "session", sessionId] as const,
  byCohort: (cohortId: string | undefined) => ["session-plan", "cohort", cohortId] as const,
  sessionRow: (sessionId: string | undefined) =>
    ["session-plan", "session-row", sessionId] as const,
  directory: (courseId: string | undefined) => ["session-plan", "directory", courseId] as const,
  myProgress: (userId: string | undefined, courseId: string | undefined) =>
    ["session-plan", "progress", userId, courseId] as const,
  mySubmissions: (userId: string | undefined) => ["session-plan", "submissions", userId] as const,
  myWatch: (userId: string | undefined) => ["session-plan", "watch", userId] as const,
};

/** Ítems del plan de una sesión (activos), ordenados por fase y posición. */
export function getSessionModulesBySessionQuery(db: PowerSyncDrizzleDB, sessionId: string) {
  return db
    .select()
    .from(session_module)
    .where(and(eq(session_module.class_session_id, sessionId), isNull(session_module.deleted_at)))
    .orderBy(asc(session_module.phase), asc(session_module.position));
}

/** Todos los ítems de plan de una cohorte (para badges "tiene plan" en el horario). */
export function getSessionModulesByCohortQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(session_module)
    .where(and(eq(session_module.cohort_id, cohortId), isNull(session_module.deleted_at)));
}

/** Una class_session por id (para la cabecera de la vista de plan del alumno). */
export function getClassSessionRowQuery(db: PowerSyncDrizzleDB, sessionId: string) {
  return db
    .select()
    .from(class_session)
    .where(and(eq(class_session.id, sessionId), isNull(class_session.deleted_at)))
    .limit(1);
}

export interface CourseModuleDirectoryRow {
  id: string;
  kind: string | null;
  ref_id: string | null;
  lesson_title: string | null;
  resource_title: string | null;
  assignment_title: string | null;
  video_title: string | null;
}

/**
 * Directorio de módulos de un curso con el TÍTULO resuelto de su entidad de
 * detalle (lección/recurso/tarea/vídeo). Un solo query local-first: el ref_id es
 * único por tabla, así que los left-join no expanden filas. La UI compone el
 * título por kind y lo usa para pintar el plan y el selector "añadir módulo".
 */
export function getCourseModuleDirectoryQuery(db: PowerSyncDrizzleDB, courseId: string) {
  return db
    .select({
      id: course_module.id,
      kind: course_module.kind,
      ref_id: course_module.ref_id,
      lesson_title: lesson.title,
      resource_title: resource.title,
      assignment_title: assignment.title,
      video_title: video_asset.title,
    })
    .from(course_module)
    .leftJoin(lesson, eq(course_module.ref_id, lesson.id))
    .leftJoin(resource, eq(course_module.ref_id, resource.id))
    .leftJoin(assignment, eq(course_module.ref_id, assignment.id))
    .leftJoin(video_asset, eq(course_module.ref_id, video_asset.id))
    .where(and(eq(course_module.course_id, courseId), isNull(course_module.deleted_at)))
    .orderBy(asc(course_module.position));
}

/** Resuelve el título de una fila del directorio según su kind. */
export function directoryRowTitle(row: CourseModuleDirectoryRow): string | null {
  switch (row.kind) {
    case "lesson": {
      return row.lesson_title;
    }
    case "resource": {
      return row.resource_title;
    }
    case "assignment": {
      return row.assignment_title;
    }
    case "video": {
      return row.video_title;
    }
    default: {
      return null;
    }
  }
}
